import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { setSession as setReduxSession, setScreenAccess, setAdminUserId } from './store/slices/authSlice';
import { useScreenAccess, checkScreenAccess } from './hooks/useScreenAccess';
import { fetchAdminProfile } from './store/slices/usersSlice';
import { RootState } from './store';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import UserTabs from './components/UserTabs/UserTabs';
import Login from './components/auth/Login';
import { remoteConfig } from './services/remoteConfigService';
import { clearSession } from './services/sessionService';
import { farmvestService } from './services/farmvest_api';
import { ALL_KEYS } from './FarmvestComponents/ScreenAccessModal';

// Public Pages
import FarmvestUserActivationPage from './components/public/FarmvestUserActivationPage';
import UnitsCelebration from './FarmvestComponents/UnitsCelebration';

// Privacy
import PrivacyPolicy from './components/PrivacyPolicy';
import Support from './components/Support';

// Skeletons
import UsersPageSkeleton from './components/common/skeletons/UsersPageSkeleton';
import OrdersPageSkeleton from './components/common/skeletons/OrdersPageSkeleton';

const AUTHORIZED_ROLES = [
  'super admin',
  'superadmin',
  'admin',
  'farmvest admin',
];

// Ordered list of screens — determines initial landing page after login
const SCREEN_ROUTE_ORDER: { key: string; path: string }[] = [
  { key: 'DASHBOARD', path: '/farmvest/dashboard' },
  { key: 'EMPLOYEES', path: '/farmvest/employees' },
  { key: 'BUFFALO', path: '/farmvest/buffalo' },
  { key: 'FARMS', path: '/farmvest/farms' },
  { key: 'TICKETS', path: '/farmvest/tickets' },
  { key: 'LEAVE_REQUESTS', path: '/farmvest/leave-requests' },
  { key: 'MILK_PRODUCTION', path: '/farmvest/milk-production' },
  { key: 'INVENTORY', path: '/farmvest/inventory' },
  { key: 'INVESTORS', path: '/farmvest/investors' },
  { key: 'INVESTOR_ONBOARDING', path: '/farmvest/investor-onboarding' },
  { key: 'WALLET', path: '/farmvest/wallet' },
  { key: 'PAYMENTS', path: '/farmvest/payments' },
  { key: 'REVENUE', path: '/farmvest/due-payouts' },
  { key: 'ROSTER', path: '/farmvest/roster' },
  { key: 'IOT_ALERTS', path: '/farmvest/iot-alerts' },
  { key: 'UNIT_TRANSFER', path: '/farmvest/unit-transfer-requests' },
  { key: 'COMPANY_OVERVIEW', path: '/farmvest/company-overview' },
  { key: 'FARM_VISIT', path: '/farmvest/farm-visit' },
  { key: 'ANIMAL_ONBOARDING', path: '/farmvest/animal-onboarding' },
  { key: 'UNALLOCATED_ANIMALS', path: '/farmvest/unallocated-animals' },
];

function getFirstAccessiblePath(screenAccess: Record<string, string>, adminRole: string): string {
  const role = (adminRole || '').toLowerCase().replace(/\s+/g, '_');
  const isSuperAdmin = role === 'super_admin' || role === 'superadmin';
  if (isSuperAdmin) return '/farmvest/dashboard';

  if (!screenAccess || Object.keys(screenAccess).length === 0) return '/farmvest/dashboard';

  // Find the first screen explicitly allowed (view or full)
  for (const { key, path } of SCREEN_ROUTE_ORDER) {
    const level = screenAccess[key];
    if (level === 'full' || level === 'view') {
      return path;
    }
  }
  return '/farmvest/dashboard';
}

// FarmVest Components (Lazy Loaded)
const FarmVestEmployees = React.lazy(() => import('./FarmvestComponents/Employees'));
const FarmVestFarms = React.lazy(() => import('./FarmvestComponents/Farms'));
const FarmVestFarmDetails = React.lazy(() => import('./FarmvestComponents/FarmDetails'));
const FarmVestEmployeeDetails = React.lazy(() => import('./FarmvestComponents/EmployeeDetailsPage'));
const FarmVestAnimalOnboarding = React.lazy(() => import('./FarmvestComponents/AnimalOnboarding/AnimalOnboarding'));
const FarmVestUnallocatedAnimals = React.lazy(() => import('./FarmvestComponents/UnallocatedAnimals/UnallocatedAnimals'));
const FarmVestUnallocatedAnimalss = React.lazy(() => import('./FarmvestComponents/UnallocatedAnimals/UnallocatedAnimalss'));
const FarmVestInvestors = React.lazy(() => import('./FarmvestComponents/Investors'));
const FarmVestInvestorDetails = React.lazy(() => import('./FarmvestComponents/InvestorDetailsPage'));
const FarmVestInventory = React.lazy(() => import('./FarmvestComponents/Inventory'));
const FarmVestBuffalo = React.lazy(() => import('./FarmvestComponents/Buffalo'));
const FarmVestTickets = React.lazy(() => import('./FarmvestComponents/Tickets'));
const FarmVestLeaveRequests = React.lazy(() => import('./FarmvestComponents/LeaveRequests'));
const FarmVestUnitTransfers = React.lazy(() => import('./FarmvestComponents/UnitTransferRequests'));
const FarmVestMilkProduction = React.lazy(() => import('./FarmvestComponents/MilkProduction'));
// const FarmVestAlertsManagement = React.lazy(() => import('./FarmvestComponents/AlertsManagement'));
const FarmVestIoTAlerts = React.lazy(() => import('./FarmvestComponents/IoTAlertsTab'));
const FarmVestAccountDeletion = React.lazy(() => import('./FarmvestComponents/AccountDeletion'));
// const FarmVestDashboard = React.lazy(() => import('./FarmvestComponents/Dashboard'));
const FarmVestPayments = React.lazy(() => import('./FarmvestComponents/Payments'));
const FarmVestRoster = React.lazy(() => import('./FarmvestComponents/RosterTab'));
const FarmVestFarmVisit = React.lazy(() => import('./FarmvestComponents/FarmVisit/FarmVisit'));
const FarmVestComingSoon = React.lazy(() => import('./FarmvestComponents/ComingSoon'));
const FarmVestInvestorOnboarding = React.lazy(() => import('./FarmvestComponents/InvestorOnboarding'));
const InvestorOnboardingForm = React.lazy(() => import('./FarmvestComponents/InvestorOnboardingForm'));

const FarmVestWallet = React.lazy(() => import('./FarmvestComponents/Wallet'));
const FarmVestDuePayouts = React.lazy(() => import('./FarmvestComponents/PayoutsDue'));
const FarmVestCompanyOverview = React.lazy(() => import('./FarmvestComponents/CompanyOverview'));


interface Session {
  mobile: string;
  role: string | null;
  name?: string;
  location_name?: string;
  farm_id?: string;
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  lastLoginTime?: string;
  currentLoginTime?: string;
  user_id?: number | null;
  screen_access?: Record<string, string>;
  roles?: string[];
}

interface ErrorBoundaryState { hasError: boolean; error?: Error }

// A failed `React.lazy(() => import(...))` surfaces as a ChunkLoadError. This
// almost always means a new build was deployed while the user had the app open,
// so the loaded runtime references chunk filenames that no longer exist on the
// server. The only reliable recovery is a full page reload to fetch fresh HTML
// and the current chunk names.
const isChunkLoadError = (error?: Error): boolean => {
  if (!error) return false;
  const name = (error as any).name || '';
  const message = error.message || '';
  return name === 'ChunkLoadError' || /Loading chunk .* failed/i.test(message) || /Loading CSS chunk .* failed/i.test(message);
};

// A `React.lazy(() => import(...))` whose module resolves with `default: undefined`
// (e.g. a stale dev hot-update, or a transiently bad module evaluation) surfaces as
// "Element type is invalid. Received a promise that resolves to: undefined / Lazy
// element type must resolve to a class or function." The chunk downloaded fine, so
// this is NOT a ChunkLoadError — but the recovery is the same: a full reload re-runs
// the lazy import cleanly.
const isLazyResolveError = (error?: Error): boolean => {
  if (!error) return false;
  const message = error.message || '';
  return /Lazy element type must resolve/i.test(message) || /Received a promise that resolves to/i.test(message);
};

// Both failure modes are recoverable by a single full page reload.
const isRecoverableLoadError = (error?: Error): boolean =>
  isChunkLoadError(error) || isLazyResolveError(error);

const CHUNK_RELOAD_KEY = 'ak_chunk_reload_at';
const CHUNK_RELOAD_WINDOW_MS = 10000;

// True only if we reloaded very recently — i.e. the reload did NOT fix the
// chunk error (the file is genuinely gone). Outside the window we allow a fresh
// auto-reload, so a chunk error later in the session still recovers.
const reloadedRecently = (): boolean => {
  const at = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
  return at > 0 && Date.now() - at < CHUNK_RELOAD_WINDOW_MS;
};

class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);

    // On a recoverable load failure (missing chunk, or a lazy module that
    // resolved to undefined), auto-reload once. A sessionStorage flag prevents an
    // infinite reload loop if the problem genuinely persists after reloading.
    if (isRecoverableLoadError(error) && !reloadedRecently()) {
      window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
      window.location.reload();
    }
  }

  handleRetry = () => {
    if (isRecoverableLoadError(this.state.error)) {
      // Manual retry: force a fresh load rather than re-rendering the cached
      // failed lazy import. Clear the guard so the reload is always allowed.
      window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      window.location.reload();
      return;
    }
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      const chunkError = isRecoverableLoadError(this.state.error);
      return this.props.fallback ?? (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h3>Something went wrong loading this page.</h3>
          {chunkError ? (
            <p style={{ color: '#475569', maxWidth: '600px', margin: '12px auto' }}>
              A new version of the app is available. Reloading to get the latest update.
            </p>
          ) : this.state.error && (
            <pre style={{ fontSize: '11px', color: '#dc2626', background: '#fef2f2', padding: '12px', borderRadius: '8px', textAlign: 'left', maxWidth: '600px', margin: '12px auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {this.state.error.message}
            </pre>
          )}
          <button onClick={this.handleRetry}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}



function App() {
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  useEffect(() => {
    const initApp = async () => {
      try {
        await Promise.race([
          remoteConfig.fetchConfig(),
          new Promise(resolve => setTimeout(resolve, 5000)),
        ]);
      } catch (_) { }
      setIsConfigLoaded(true);
    };
    initApp();
  }, []);

  const [session, setSession] = useState<Session | null>(() => {
    const saved = window.localStorage.getItem('ak_dashboard_session');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        window.localStorage.removeItem('ak_dashboard_session');
      }
    }
    return null;
  });
  const dispatch = useAppDispatch();
  const { adminProfile } = useAppSelector((state: RootState) => state.users);
  const navigate = useNavigate();
  const location = useLocation();

  const dispatchedProfileFetch = useRef<string | null>(null);
  const lastFetchedAccessRef = useRef<{ userId: any; roles: string }>({ userId: null, roles: '' });

  useEffect(() => {
    if (!session) {
      dispatchedProfileFetch.current = null;
      return;
    }
    dispatch(setReduxSession({
      adminMobile: session.mobile,
      adminName: session.name || 'Admin',
      adminRole: session.role || 'Admin',
      adminFarmId: session.farm_id || '',
      locationName: session.location_name || '',
      lastLogin: session.lastLoginTime || 'First Login',
      presentLogin: session.currentLoginTime || new Date().toLocaleString(),
      adminUserId: (session as any).user_id || null,
      screenAccess: (session as any).screen_access || {},
    }));
    if ((session as any).user_id) dispatch(setAdminUserId((session as any).user_id));
    if ((session as any).screen_access) dispatch(setScreenAccess((session as any).screen_access));

    // Fetch admin profile if not already loaded to prevent repeated API calls
    // Skip for specific test users causing 500 errors or Farmvest admins not in Animalkart DB
    if (!adminProfile && session.mobile !== '1234567891' && dispatchedProfileFetch.current !== session.mobile) {
      dispatchedProfileFetch.current = session.mobile;
      dispatch(fetchAdminProfile(session.mobile));
    } else if (adminProfile) {
      // Sync Logic:
      // Only update if the current session role is invalid/missing, OR if the profile role is actually 'better' or 'authorized'.
      // PROBLEM FIX: The profile returns 'SpecialCategory' but login returns 'ADMIN'. We must NOT overwrite 'ADMIN' with 'SpecialCategory'.

      const currentRole = session.role;
      const profileRole = adminProfile.role;

      // Resolve profile name
      const profileName = (
        adminProfile.first_name || adminProfile.last_name
          ? `${adminProfile.first_name || ''} ${adminProfile.last_name || ''}`.trim()
          : adminProfile.name || adminProfile.full_name
      ) || session.name;

      const targetRole = profileRole || currentRole;
      const targetUserId = adminProfile.user_id || adminProfile.id || (session as any).user_id;

      // If targetRole differs from current session role OR name differs OR user_id differs, sync!
      if (targetRole !== currentRole || session.name !== profileName || (session as any).user_id !== targetUserId) {
        const updatedSession = {
          ...session,
          role: targetRole,
          name: profileName,
          user_id: targetUserId
        };

        if (JSON.stringify(updatedSession) !== JSON.stringify(session)) {
          setSession(updatedSession);
          window.localStorage.setItem('ak_dashboard_session', JSON.stringify(updatedSession));
        }
      }
    }
  }, [dispatch, session, adminProfile]);

  // Centralized hook to fetch role templates and merge permissions dynamically
  useEffect(() => {
    if (!session) {
      lastFetchedAccessRef.current = { userId: null, roles: '' };
      return;
    }

    let isMounted = true;
    const loadAndMergePermissions = async () => {
      try {
        // Collect all roles from session and adminProfile
        let empRoles: string[] = [];
        if (Array.isArray((session as any).roles)) {
          empRoles.push(...(session as any).roles);
        }
        if (session.role && !empRoles.includes(session.role)) {
          empRoles.push(session.role);
        }
        if (adminProfile && Array.isArray(adminProfile.roles_detail)) {
          adminProfile.roles_detail.forEach((rd: any) => {
            if (rd.role && !empRoles.includes(rd.role)) {
              empRoles.push(rd.role);
            }
          });
        }

        // If user has multiple roles and one is INVESTOR, bypass/exclude INVESTOR
        const hasOtherRole = empRoles.some(r => r.toUpperCase() !== 'INVESTOR');
        if (hasOtherRole) {
          empRoles = empRoles.filter(r => r.toUpperCase() !== 'INVESTOR');
        }

        const userId = (session as any).user_id;
        const currentRolesStr = empRoles.sort().join(',');
        if (userId === lastFetchedAccessRef.current.userId && currentRolesStr === lastFetchedAccessRef.current.roles) {
          return;
        }

        lastFetchedAccessRef.current = { userId, roles: currentRolesStr };

        // Fetch user-specific overrides from API if userId exists
        let userAccessUpper: Record<string, string> = {};
        if (userId) {
          try {
            const userAccessRes = await farmvestService.getScreenAccess(userId);
            if (userAccessRes && userAccessRes.data?.screen_access) {
              Object.entries(userAccessRes.data.screen_access).forEach(([k, v]) => {
                userAccessUpper[k.toUpperCase()] = String(v);
              });
            }
          } catch (fetchErr) {
            console.error('Failed to fetch user-specific screen access overrides:', fetchErr);
            // Fallback to local session.screen_access if API fails
            const localUserAccess = (session as any).screen_access || {};
            Object.entries(localUserAccess).forEach(([k, v]) => {
              userAccessUpper[k.toUpperCase()] = String(v);
            });
          }
        } else {
          const localUserAccess = (session as any).screen_access || {};
          Object.entries(localUserAccess).forEach(([k, v]) => {
            userAccessUpper[k.toUpperCase()] = String(v);
          });
        }

        const merged: Record<string, string> = {};
        ALL_KEYS.forEach(key => {
          merged[key] = userAccessUpper[key] || 'none';
        });

        // 1. Dispatch to Redux store
        dispatch(setScreenAccess(merged));

        // 2. Cache back to localStorage and session state if changed to avoid flicker on reload
        const currentAccessStr = JSON.stringify((session as any).screen_access || {});
        const mergedAccessStr = JSON.stringify(merged);
        if (currentAccessStr !== mergedAccessStr) {
          const updatedSession = {
            ...session,
            screen_access: merged
          };
          setSession(updatedSession);
          window.localStorage.setItem('ak_dashboard_session', JSON.stringify(updatedSession));
        }
      } catch (err) {
        console.error('Error fetching role templates for merging:', err);
        // Fallback to local overrides if API fails
        const fallbackMerged: Record<string, string> = {};
        const userAccess = (session as any).screen_access || {};
        const userAccessUpper: Record<string, string> = {};
        Object.entries(userAccess).forEach(([k, v]) => {
          userAccessUpper[k.toUpperCase()] = String(v);
        });
        ALL_KEYS.forEach(key => {
          fallbackMerged[key] = userAccessUpper[key] || 'none';
        });
        if (isMounted) {
          dispatch(setScreenAccess(fallbackMerged));
        }
      }
    };

    loadAndMergePermissions();
    return () => { isMounted = false; };
  }, [
    session?.role,
    (session as any)?.user_id,
    JSON.stringify((session as any)?.screen_access),
    JSON.stringify((session as any)?.roles),
    JSON.stringify(adminProfile?.roles_detail),
    dispatch
  ]);

  const handleLogin = useCallback(async (s: Session) => {
    // Determine last login (from previous session or current if new)
    const prevSessionStr = window.localStorage.getItem('ak_dashboard_session');
    let lastLoginTime = new Date().toLocaleString();

    if (prevSessionStr) {
      try {
        const prevSession = JSON.parse(prevSessionStr);
        if (prevSession.currentLoginTime) {
          lastLoginTime = prevSession.currentLoginTime;
        }
      } catch (e) { }
    }

    const newSession = {
      ...s,
      currentLoginTime: new Date().toLocaleString(),
      lastLoginTime: lastLoginTime
    };

    // Fetch and merge permissions immediately to determine the correct landing page
    let mergedScreenAccess: Record<string, string> = { ...newSession.screen_access };
    try {
      let empRoles: string[] = [];
      if (Array.isArray(newSession.roles)) {
        empRoles.push(...newSession.roles);
      }
      if (newSession.role && !empRoles.includes(newSession.role)) {
        empRoles.push(newSession.role);
      }

      // If user has multiple roles and one is INVESTOR, bypass/exclude INVESTOR
      const hasOtherRole = empRoles.some(r => r.toUpperCase() !== 'INVESTOR');
      if (hasOtherRole) {
        empRoles = empRoles.filter(r => r.toUpperCase() !== 'INVESTOR');
      }

      const userAccessRes = newSession.user_id 
        ? await farmvestService.getScreenAccess(newSession.user_id).catch(() => null) 
        : null;

      const userAccessUpper: Record<string, string> = {};
      const localUserAccess = newSession.screen_access || {};
      Object.entries(localUserAccess).forEach(([k, v]) => {
        userAccessUpper[k.toUpperCase()] = String(v);
      });
      if (userAccessRes && userAccessRes.data?.screen_access) {
        Object.entries(userAccessRes.data.screen_access).forEach(([k, v]) => {
          userAccessUpper[k.toUpperCase()] = String(v);
        });
      }
      ALL_KEYS.forEach(key => {
        mergedScreenAccess[key] = userAccessUpper[key] || 'none';
      });
      newSession.screen_access = mergedScreenAccess;

      // Populate ref to prevent duplicate screen access/role template fetches on first render
      const loginRoles: string[] = [];
      if (Array.isArray(newSession.roles)) {
        loginRoles.push(...newSession.roles);
      }
      if (newSession.role && !loginRoles.includes(newSession.role)) {
        loginRoles.push(newSession.role);
      }
      lastFetchedAccessRef.current = {
        userId: newSession.user_id,
        roles: loginRoles.sort().join(',')
      };
    } catch (err) {
      console.error('Error fetching role templates during login:', err);
    }

    window.localStorage.setItem('ak_dashboard_session', JSON.stringify(newSession));
    setSession(newSession);
    dispatch(setReduxSession({
      adminMobile: newSession.mobile,
      adminName: newSession.name || 'Admin',
      adminRole: newSession.role || 'Admin',
      adminFarmId: (newSession as any).farm_id || '',
      locationName: newSession.location_name || '',
      lastLogin: newSession.lastLoginTime || 'First Login',
      presentLogin: newSession.currentLoginTime || new Date().toLocaleString(),
      adminUserId: (newSession as any).user_id || null,
      screenAccess: newSession.screen_access || {},
    }));

    // Navigate to the first screen the user has access to
    const screenAccess = newSession.screen_access || {};
    const defaultPath = getFirstAccessiblePath(screenAccess, newSession.role || '');

    // Navigate to origin or default
    // Prefer React Router location.state; fall back to sessionStorage for WhatsApp/in-app browsers
    const from = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
    const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
    sessionStorage.removeItem('redirectAfterLogin');
    const targetPath = (from && from.pathname !== '/login')
      ? (from.pathname + (from.search || ''))
      : (savedRedirect && savedRedirect !== '/login')
        ? savedRedirect
        : defaultPath;

    navigate(targetPath, { replace: true });
  }, [dispatch, location.state, navigate]);

  const handleLogout = async () => {
    // Await so the IDB wipe commits before the route change unmounts us.
    await clearSession();
    setSession(null);
    dispatch({ type: 'USER_LOGOUT' });
  };




  const checkAdmin = () => {
    // Any authenticated user with a role is allowed in.
    // Screen-level access is controlled by useScreenAccess (screen_access config from backend).
    return !!session?.role;
  };

  const isAdmin = checkAdmin();

  if (!isConfigLoaded) {
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route path="/login" element={
          session ? <DefaultRedirect session={session} /> : <Login onLogin={handleLogin} />
        } />

        {/* TV Screen — public, no auth required */}
        <Route path="/tv/units" element={<UnitsCelebration />} />

        {/* FarmVest Routes */}
        <Route path="/farmvest/employees" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="EMPLOYEES">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestEmployees />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/employees/details" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="EMPLOYEES">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestEmployeeDetails />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/farms" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="FARMS">
            <ErrorBoundary><React.Suspense fallback={<OrdersPageSkeleton />}>
              <FarmVestFarms />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/farms/details" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="FARMS">
            <ErrorBoundary><React.Suspense fallback={<OrdersPageSkeleton />}>
              <FarmVestFarmDetails />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/animal-onboarding" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="ANIMAL_ONBOARDING">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestAnimalOnboarding />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/unallocated-animals" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="UNALLOCATED_ANIMALS">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestUnallocatedAnimals />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/unallocated-animalss" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="UNALLOCATED_ANIMALS,ANIMAL_ONBOARDING">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestUnallocatedAnimalss />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/investors" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="INVESTORS">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestInvestors />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/investors/details" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="INVESTORS">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestInvestorDetails />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/inventory" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="INVENTORY">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestInventory />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/buffalo" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="BUFFALO">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestBuffalo />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/tickets" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="TICKETS">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestTickets />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/leave-requests" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="LEAVE_REQUESTS">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestLeaveRequests />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/unit-transfer-requests" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="UNIT_TRANSFER">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestUnitTransfers />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/milk-production" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="MILK_PRODUCTION">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestMilkProduction />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        {/* <Route path="/farmvest/alerts" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout}>
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestAlertsManagement />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } /> */}

        <Route path="/farmvest/iot-alerts" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="IOT_ALERTS">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestIoTAlerts />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/wallet" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="WALLET">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestWallet />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/due-payouts" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="REVENUE">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestDuePayouts />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/payments" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="PAYMENTS">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestPayments />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />



        <Route path="/farmvest/roster" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="ROSTER">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestRoster />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        {/* <Route path="/farmvest/dashboard" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="DASHBOARD">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestDashboard />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } /> */}

        <Route path="/farmvest/dashboard" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="DASHBOARD">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestCompanyOverview />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/farm-visit" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="FARM_VISIT">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestFarmVisit />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        {/* <Route path="/farmvest/onboarding-process" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout}>
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestOnboardingProcess />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } /> */}

        <Route path="/farmvest/coming-soon" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout}>
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestComingSoon />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        <Route path="/farmvest/account-deletion" element={
          <ConditionalLayoutWrapper session={session} handleLogout={handleLogout}>
            <FarmVestAccountDeletion />
          </ConditionalLayoutWrapper>
        } />

        <Route path="/farmvest/user-activation" element={
          <ConditionalLayoutWrapper session={session} handleLogout={handleLogout}>
            <FarmvestUserActivationPage />
          </ConditionalLayoutWrapper>
        } />

        {/* TV Celebration Screen — public, no auth required */}
        <Route path="/tv/units" element={<UnitsCelebration />} />

        {/* Privacy Policy */}
        <Route path="/privacy-policy" element={
          <ConditionalLayoutWrapper session={session} handleLogout={handleLogout}>
            <PrivacyPolicy />
          </ConditionalLayoutWrapper>
        } />

        {/* Support Page */}
        <Route path="/support" element={
          <ConditionalLayoutWrapper session={session} handleLogout={handleLogout}>
            <Support />
          </ConditionalLayoutWrapper>
        } />

        <Route path="/farmvest/investor-onboarding" element={
          <ProtectedRoute session={session} isAdmin={isAdmin} handleLogout={handleLogout} screenKey="INVESTOR_ONBOARDING">
            <ErrorBoundary><React.Suspense fallback={<UsersPageSkeleton />}>
              <FarmVestInvestorOnboarding />
            </React.Suspense></ErrorBoundary>
          </ProtectedRoute>
        } />

        {/* Public onboarding form — no auth required */}
        <Route path="/onboard" element={
          <React.Suspense fallback={<div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>}>
            <InvestorOnboardingForm />
          </React.Suspense>
        } />

        {/* Default redirect — lands on the first accessible screen for this role */}
        <Route path="/" element={session ? <DefaultRedirect session={session} /> : <Navigate to="/login" replace />} />
        <Route path="*" element={session ? <DefaultRedirect session={session} /> : <Navigate to="/login" replace />} />
      </Routes>
    </div>
  );
}

// Redirects to the first screen this user can access
const DefaultRedirect = ({ session }: { session: Session | null }) => {
  const { adminRole, screenAccess } = useAppSelector((state: RootState) => state.auth);
  if (!session) return <Navigate to="/login" replace />;
  const path = getFirstAccessiblePath(screenAccess, adminRole);
  return <Navigate to={path} replace />;
};

const NoAccessPage = () => (
  <div className="flex flex-col items-center justify-center h-full py-24 gap-4">
    <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
      </svg>
    </div>
    <h2 className="text-lg font-bold text-gray-800">Access Restricted</h2>
    <p className="text-sm text-gray-500 text-center max-w-xs">You don't have permission to view this screen. Contact your administrator to request access.</p>
  </div>
);

const ScreenGuard = ({ screenKey, children }: { screenKey: string; children: React.ReactNode }) => {
  const { adminRole, screenAccess } = useAppSelector((state: RootState) => state.auth);
  const keys = screenKey.split(',');
  const hasAccess = keys.some(key => {
    const { canView } = checkScreenAccess(key.trim(), screenAccess, adminRole);
    return canView;
  });
  if (!hasAccess) return <NoAccessPage />;
  return <>{children}</>;
};

const ProtectedRoute = ({ children, session, isAdmin, handleLogout, screenKey }: { children: React.ReactNode, session: Session | null, isAdmin: boolean, handleLogout: () => void, screenKey?: string }) => {
  const location = useLocation();
  const isPublicAIEntry = location.pathname === '/farmvest/tickets' && location.search.includes('view=AI_ENTRY');

  // Allow public access to AI Entry form
  if (!session && isPublicAIEntry) {
    return (
      <div style={{ height: '100vh', background: '#f8fafc', overflow: 'auto' }}>
        {children}
      </div>
    );
  }

  if (!session) {
    // Save intended URL to sessionStorage as fallback for WhatsApp/in-app browsers
    // where React Router location.state doesn't survive the redirect
    if (location.pathname !== '/login') {
      sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    }
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  // Simplified check for Farmvest Dashboard
  if (!isAdmin) {
    return (
      <div style={{ maxWidth: 600, margin: '2rem auto', padding: '1.5rem', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center' }}>
        <h2 style={{ marginBottom: '0.75rem' }}>Access Restricted</h2>
        <p style={{ marginBottom: 0 }}>Only authorized Admin users can access this dashboard. Please login with an Admin mobile.</p>
        <button onClick={handleLogout} style={{ marginTop: '1rem', padding: '8px 16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Logout</button>
      </div>
    );
  }

  return (
    <UserTabs
      adminMobile={session.mobile}
      adminName={session.name}
      adminRole={session.role || undefined}
      locationName={session.location_name}
      lastLogin={session.lastLoginTime}
      presentLogin={session.currentLoginTime}
      onLogout={handleLogout}
    >
      {screenKey ? <ScreenGuard screenKey={screenKey}>{children}</ScreenGuard> : children}
    </UserTabs>
  );
};

const ConditionalLayoutWrapper = ({ children, session, handleLogout }: { children: React.ReactNode, session: Session | null, handleLogout: () => void }) => {
  const location = useLocation();

  // Dashboard paths that should show layout if logged in
  const dashboardPaths = [
    '/farmvest/user-activation',
    '/farmvest/account-deletion',
    '/privacy-policy',
    '/support'
  ];

  const isDashboardPath = dashboardPaths.some(path => location.pathname.startsWith(path));
  const shouldShowLayout = (location.state?.fromDashboard || isDashboardPath) && session;

  if (shouldShowLayout) {
    return (
      <UserTabs
        adminMobile={session?.mobile}
        adminName={session?.name}
        adminRole={session?.role || undefined}
        locationName={session?.location_name}
        lastLogin={session?.lastLoginTime}
        presentLogin={session?.currentLoginTime}
        onLogout={handleLogout}
      >
        {children}
      </UserTabs>
    );
  }

  return <>{children}</>;
};

export default App;
